import React from 'react';
import { useTranslation } from 'react-i18next';
import { Construction, Mail, Phone } from 'lucide-react';
import StorefrontLogo from '../components/storefront/StorefrontLogo';
import StorefrontFavicon from '../components/storefront/StorefrontFavicon';
import { getStorefrontBrandName, getStorefrontContact } from '../utils/storefrontBrand';
import { DEFAULT_STOREFRONT_SETTINGS } from '../utils/storefrontThemes';

const StorefrontMaintenancePage = ({ company, storefrontSettings = DEFAULT_STOREFRONT_SETTINGS }) => {
  const { t } = useTranslation();
  const contact = getStorefrontContact(company, storefrontSettings);
  const title = storefrontSettings?.maintenanceTitle
    || t('storefront.maintenance.defaultTitle');
  const message = storefrontSettings?.maintenanceMessage
    || t('storefront.maintenance.defaultMessage');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      <StorefrontFavicon storefrontSettings={storefrontSettings} company={company} />

      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-600 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-lg text-center">
        <div className="flex justify-center mb-6">
          <StorefrontLogo
            company={company}
            size="lg"
            showWordmark
            theme="dark"
            tagline={getStorefrontBrandName(t, company)}
          />
        </div>

        <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-white/10 border border-white/20 mb-6">
          <Construction className="h-8 w-8 text-amber-300" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
          {title}
        </h1>
        <p className="text-blue-100/90 text-sm sm:text-base leading-relaxed mb-8 whitespace-pre-line">
          {message}
        </p>

        <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-5 space-y-3 text-left">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-200/80 text-center">
            {t('storefront.maintenance.contactHint')}
          </p>
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-3 text-sm text-white/90 hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4 text-blue-300 shrink-0" />
              {contact.phone}
            </a>
          )}
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-3 text-sm text-white/90 hover:text-white transition-colors"
          >
            <Mail className="h-4 w-4 text-blue-300 shrink-0" />
            {contact.email}
          </a>
        </div>

        <p className="mt-8 text-xs text-blue-200/50">
          © {new Date().getFullYear()} {getStorefrontBrandName(t, company)}
        </p>
      </div>
    </div>
  );
};

export default StorefrontMaintenancePage;
